package com.yet.tetris.database.database

import app.cash.sqldelight.SuspendingTransacterImpl
import app.cash.sqldelight.db.AfterVersion
import app.cash.sqldelight.db.QueryResult
import app.cash.sqldelight.db.SqlDriver
import app.cash.sqldelight.db.SqlSchema
import com.yet.tetris.database.BoardCells
import com.yet.tetris.database.BoardCellsQueries
import com.yet.tetris.database.CurrentGameState
import com.yet.tetris.database.CurrentGameStateQueries
import com.yet.tetris.database.GameHistory
import com.yet.tetris.database.GameHistoryQueries
import com.yet.tetris.database.TetrisLiteDatabase
import kotlin.Long
import kotlin.Unit
import kotlin.reflect.KClass

internal val KClass<TetrisLiteDatabase>.schema: SqlSchema<QueryResult.AsyncValue<Unit>>
  get() = TetrisLiteDatabaseImpl.Schema

internal fun KClass<TetrisLiteDatabase>.newInstance(
  driver: SqlDriver,
  BoardCellsAdapter: BoardCells.Adapter,
  CurrentGameStateAdapter: CurrentGameState.Adapter,
  GameHistoryAdapter: GameHistory.Adapter,
): TetrisLiteDatabase = TetrisLiteDatabaseImpl(driver, BoardCellsAdapter, CurrentGameStateAdapter, GameHistoryAdapter)

private class TetrisLiteDatabaseImpl(
  driver: SqlDriver,
  BoardCellsAdapter: BoardCells.Adapter,
  CurrentGameStateAdapter: CurrentGameState.Adapter,
  GameHistoryAdapter: GameHistory.Adapter,
) : SuspendingTransacterImpl(driver),
    TetrisLiteDatabase {
  override val boardCellsQueries: BoardCellsQueries = BoardCellsQueries(driver, BoardCellsAdapter)

  override val currentGameStateQueries: CurrentGameStateQueries =
      CurrentGameStateQueries(driver, CurrentGameStateAdapter)

  override val gameHistoryQueries: GameHistoryQueries =
      GameHistoryQueries(driver, GameHistoryAdapter)

  public object Schema : SqlSchema<QueryResult.AsyncValue<Unit>> {
    override val version: Long
      get() = 4

    override fun create(driver: SqlDriver): QueryResult.AsyncValue<Unit> = QueryResult.AsyncValue {
      driver.execute(null, """
          |CREATE TABLE IF NOT EXISTS BoardCells (
          |    positionX INTEGER NOT NULL,
          |    positionY INTEGER NOT NULL,
          |    pieceType TEXT NOT NULL,
          |    PRIMARY KEY (positionX, positionY)
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE IF NOT EXISTS CurrentGameState (
          |    id INTEGER NOT NULL PRIMARY KEY DEFAULT 1,
          |    score INTEGER NOT NULL,
          |    linesCleared INTEGER NOT NULL,
          |    level INTEGER NOT NULL DEFAULT 1,
          |    piecesPlaced INTEGER NOT NULL DEFAULT 0,
          |    maxCombo INTEGER NOT NULL DEFAULT 0,
          |    tetrisesCleared INTEGER NOT NULL DEFAULT 0,
          |    tSpinClears INTEGER NOT NULL DEFAULT 0,
          |    perfectClears INTEGER NOT NULL DEFAULT 0,
          |    hardDrops INTEGER NOT NULL DEFAULT 0,
          |    hardDropCells INTEGER NOT NULL DEFAULT 0,
          |    softDropCells INTEGER NOT NULL DEFAULT 0,
          |    backToBackChain INTEGER NOT NULL DEFAULT 0,
          |    isTSpinEligible INTEGER NOT NULL DEFAULT 0,
          |    currentPieceType TEXT,
          |    currentPieceRotation INTEGER NOT NULL DEFAULT 0,
          |    currentPositionX INTEGER NOT NULL,
          |    currentPositionY INTEGER NOT NULL,
          |    nextPieceType TEXT NOT NULL,
          |    nextPieceRotation INTEGER NOT NULL DEFAULT 0,
          |    nextQueue TEXT NOT NULL DEFAULT '',
          |    holdPieceType TEXT,
          |    holdPieceRotation INTEGER NOT NULL DEFAULT 0,
          |    canHold INTEGER NOT NULL DEFAULT 1,
          |    isGameOver INTEGER NOT NULL DEFAULT 0,
          |    isPaused INTEGER NOT NULL DEFAULT 0,
          |    boardWidth INTEGER NOT NULL DEFAULT 10,
          |    boardHeight INTEGER NOT NULL DEFAULT 20
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE IF NOT EXISTS GameHistory (
          |    id TEXT NOT NULL PRIMARY KEY,
          |    score INTEGER NOT NULL,
          |    linesCleared INTEGER NOT NULL,
          |    level INTEGER NOT NULL DEFAULT 1,
          |    difficulty TEXT NOT NULL,
          |    timestamp INTEGER NOT NULL,
          |    durationMs INTEGER NOT NULL DEFAULT 0,
          |    piecesPlaced INTEGER NOT NULL DEFAULT 0,
          |    maxCombo INTEGER NOT NULL DEFAULT 0,
          |    tetrisesCleared INTEGER NOT NULL DEFAULT 0,
          |    tSpinClears INTEGER NOT NULL DEFAULT 0,
          |    perfectClears INTEGER NOT NULL DEFAULT 0,
          |    hardDrops INTEGER NOT NULL DEFAULT 0,
          |    hardDropCells INTEGER NOT NULL DEFAULT 0,
          |    softDropCells INTEGER NOT NULL DEFAULT 0
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE IF NOT EXISTS GameSettings (
          |    id INTEGER NOT NULL PRIMARY KEY DEFAULT 1,
          |    difficulty TEXT NOT NULL
          |    -- Добавьте сюда другие настройки по аналогии
          |)
          """.trimMargin(), 0).await()
    }

    private fun migrateInternal(
      driver: SqlDriver,
      oldVersion: Long,
      newVersion: Long,
    ): QueryResult.AsyncValue<Unit> = QueryResult.AsyncValue {
      if (oldVersion <= 1 && newVersion > 1) {
        driver.execute(null, """
            |ALTER TABLE CurrentGameState
            |ADD COLUMN level INTEGER NOT NULL DEFAULT 1
            """.trimMargin(), 0).await()
      }
      if (oldVersion <= 2 && newVersion > 2) {
        driver.execute(null, """
            |ALTER TABLE CurrentGameState
            |ADD COLUMN nextQueue TEXT NOT NULL DEFAULT ''
            """.trimMargin(), 0).await()
        driver.execute(null, """
            |ALTER TABLE CurrentGameState
            |ADD COLUMN holdPieceType TEXT
            """.trimMargin(), 0).await()
        driver.execute(null, """
            |ALTER TABLE CurrentGameState
            |ADD COLUMN holdPieceRotation INTEGER NOT NULL DEFAULT 0
            """.trimMargin(), 0).await()
        driver.execute(null, """
            |ALTER TABLE CurrentGameState
            |ADD COLUMN canHold INTEGER NOT NULL DEFAULT 1
            """.trimMargin(), 0).await()
      }
      if (oldVersion <= 3 && newVersion > 3) {
        driver.execute(null, """
            |ALTER TABLE CurrentGameState
            |ADD COLUMN piecesPlaced INTEGER NOT NULL DEFAULT 0
            """.trimMargin(), 0).await()
        driver.execute(null, """
            |ALTER TABLE CurrentGameState
            |ADD COLUMN maxCombo INTEGER NOT NULL DEFAULT 0
            """.trimMargin(), 0).await()
        driver.execute(null, """
            |ALTER TABLE CurrentGameState
            |ADD COLUMN tetrisesCleared INTEGER NOT NULL DEFAULT 0
            """.trimMargin(), 0).await()
        driver.execute(null, """
            |ALTER TABLE CurrentGameState
            |ADD COLUMN tSpinClears INTEGER NOT NULL DEFAULT 0
            """.trimMargin(), 0).await()
        driver.execute(null, """
            |ALTER TABLE CurrentGameState
            |ADD COLUMN perfectClears INTEGER NOT NULL DEFAULT 0
            """.trimMargin(), 0).await()
        driver.execute(null, """
            |ALTER TABLE CurrentGameState
            |ADD COLUMN hardDrops INTEGER NOT NULL DEFAULT 0
            """.trimMargin(), 0).await()
        driver.execute(null, """
            |ALTER TABLE CurrentGameState
            |ADD COLUMN hardDropCells INTEGER NOT NULL DEFAULT 0
            """.trimMargin(), 0).await()
        driver.execute(null, """
            |ALTER TABLE CurrentGameState
            |ADD COLUMN softDropCells INTEGER NOT NULL DEFAULT 0
            """.trimMargin(), 0).await()
        driver.execute(null, """
            |ALTER TABLE CurrentGameState
            |ADD COLUMN backToBackChain INTEGER NOT NULL DEFAULT 0
            """.trimMargin(), 0).await()
        driver.execute(null, """
            |ALTER TABLE CurrentGameState
            |ADD COLUMN isTSpinEligible INTEGER NOT NULL DEFAULT 0
            """.trimMargin(), 0).await()
        driver.execute(null, """
            |ALTER TABLE GameHistory
            |ADD COLUMN level INTEGER NOT NULL DEFAULT 1
            """.trimMargin(), 0).await()
        driver.execute(null, """
            |ALTER TABLE GameHistory
            |ADD COLUMN durationMs INTEGER NOT NULL DEFAULT 0
            """.trimMargin(), 0).await()
        driver.execute(null, """
            |ALTER TABLE GameHistory
            |ADD COLUMN piecesPlaced INTEGER NOT NULL DEFAULT 0
            """.trimMargin(), 0).await()
        driver.execute(null, """
            |ALTER TABLE GameHistory
            |ADD COLUMN maxCombo INTEGER NOT NULL DEFAULT 0
            """.trimMargin(), 0).await()
        driver.execute(null, """
            |ALTER TABLE GameHistory
            |ADD COLUMN tetrisesCleared INTEGER NOT NULL DEFAULT 0
            """.trimMargin(), 0).await()
        driver.execute(null, """
            |ALTER TABLE GameHistory
            |ADD COLUMN tSpinClears INTEGER NOT NULL DEFAULT 0
            """.trimMargin(), 0).await()
        driver.execute(null, """
            |ALTER TABLE GameHistory
            |ADD COLUMN perfectClears INTEGER NOT NULL DEFAULT 0
            """.trimMargin(), 0).await()
        driver.execute(null, """
            |ALTER TABLE GameHistory
            |ADD COLUMN hardDrops INTEGER NOT NULL DEFAULT 0
            """.trimMargin(), 0).await()
        driver.execute(null, """
            |ALTER TABLE GameHistory
            |ADD COLUMN hardDropCells INTEGER NOT NULL DEFAULT 0
            """.trimMargin(), 0).await()
        driver.execute(null, """
            |ALTER TABLE GameHistory
            |ADD COLUMN softDropCells INTEGER NOT NULL DEFAULT 0
            """.trimMargin(), 0).await()
      }
    }

    override fun migrate(
      driver: SqlDriver,
      oldVersion: Long,
      newVersion: Long,
      vararg callbacks: AfterVersion,
    ): QueryResult.AsyncValue<Unit> = QueryResult.AsyncValue {
      var lastVersion = oldVersion

      callbacks.filter { it.afterVersion in oldVersion until newVersion }
      .sortedBy { it.afterVersion }
      .forEach { callback ->
        migrateInternal(driver, oldVersion = lastVersion, newVersion = callback.afterVersion + 1).await()
        callback.block(driver)
        lastVersion = callback.afterVersion + 1
      }

      if (lastVersion < newVersion) {
        migrateInternal(driver, lastVersion, newVersion).await()
      }
    }
  }
}
